import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { BranchCell } from "@/components/BranchFlag";
import { PageHeader, TableShell } from "@/components/EntityManager";
import StatusBadge from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Eye, FileCheck2, Trash2, Printer, X, Download, Satellite, Radio, MessageCircle, ExternalLink, ShieldCheck } from "lucide-react";
import { inr, fmtDate } from "@/lib/format";
import { printConsignment, printConsignmentRegister, printConsignmentDetail } from "@/lib/print";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

async function printLrWithWatermark(c, isAdmin, copies = null, onCount) {
  let dup = false;
  if (c?.id) {
    try {
      const res = await api.post(`/shipments/${c.id}/mark-printed`);
      const count = res.data.print_count || 1;
      dup = !isAdmin && count > 1;
      if (onCount) onCount(count);
    } catch {
      dup = false;
    }
  }
  printConsignment(c, copies, { duplicate: dup });
}

const STATUS_FILTERS = ["All", "Pending", "In Transit", "Waiting for unloading", "Vehicle Break down", "Delivered", "Cancelled"];

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border py-2 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value || "—"}</span>
    </div>
  );
}

export default function ConsignmentEnquiry() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [noBranchOnly, setNoBranchOnly] = useState(false);
  const [detail, setDetail] = useState(null);
  const [billing, setBilling] = useState(null);
  const [podShareId, setPodShareId] = useState("");
  useEffect(() => {
    setBilling(null);
    setPodShareId("");
    if (detail?.id) {
      api.get(`/consignments/${detail.id}/billing-info`).then((r) => setBilling(r.data)).catch(() => setBilling({}));
      if (detail.pod_path) api.post("/pod-share", { shipment_id: detail.id }).then((r) => setPodShareId(r.data.id)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id]);
  const [hasSearched, setHasSearched] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [hireMap, setHireMap] = useState({});
  const [customers, setCustomers] = useState([]);
  const token = localStorage.getItem("ntc_token");

  useEffect(() => {
    const qp = new URLSearchParams(window.location.search).get("q");
    if (qp) {
      setQ(qp);
      setHasSearched(true);
    }
  }, []);

  useEffect(() => {
    api
      .get("/shipments")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api
      .get("/vehicles")
      .then((r) => setVehicles(r.data))
      .catch(() => {});
    api
      .get("/consignments/hire-map")
      .then((r) => setHireMap(r.data || {}))
      .catch(() => {});
    api
      .get("/customers")
      .then((r) => setCustomers(r.data || []))
      .catch(() => {});
  }, []);

  const codeFor = (r) => {
    const m = customers.find((c) => (r.customer_id && c.id === r.customer_id) || (c.name || "").trim().toLowerCase() === String(r.customer_name || "").trim().toLowerCase());
    return m?.customer_code || r.customer_code || "";
  };

  const findVehicle = (vno) => {
    const norm = (s) => String(s || "").replace(/\s+/g, "").toUpperCase();
    const key = norm(vno);
    if (!key) return null;
    return vehicles.find((v) => norm(v.registration_no) === key) || null;
  };

  const normCn = (v) => String(v || "").replace(/^CN[-\s]?/i, "").replace(/^0+/, "").trim().toLowerCase();
  const parseTermDate = (t) => {
    let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (m) return `${m[1]}-${String(m[2]).padStart(2, "0")}-${String(m[3]).padStart(2, "0")}`;
    m = t.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (m) return `${m[3]}-${String(m[2]).padStart(2, "0")}-${String(m[1]).padStart(2, "0")}`;
    return null;
  };

  const parseTermMonth = (t) => {
    let m = t.match(/^(\d{1,2})[-/.](\d{4})$/);
    if (m) return `${m[2]}-${String(m[1]).padStart(2, "0")}`;
    m = t.match(/^(\d{4})[-/.](\d{1,2})$/);
    if (m) return `${m[1]}-${String(m[2]).padStart(2, "0")}`;
    return null;
  };

  const filtered = useMemo(() => {
    if (!rows) return [];
    const term = q.trim().toLowerCase();
    const termDate = term ? parseTermDate(term) : null;
    const termMonth = term && !termDate ? parseTermMonth(term) : null;
    const normTerm = normCn(term);
    const hasExactLr =
      !termDate && !termMonth && normTerm && rows.some((r) => normCn(r.lr_no) === normTerm || normCn(r.consignment_no) === normTerm);
    return rows.filter((r) => {
      if (status !== "All" && r.status !== status) return false;
      if (noBranchOnly && (r.office || r.office_name)) return false;
      const d = (r.lr_date || r.dispatch_date || "").slice(0, 10);
      if (fromDate && (!d || d < fromDate)) return false;
      if (toDate && (!d || d > toDate)) return false;
      if (!term) return true;
      if (termDate) return d === termDate;
      if (termMonth) return d.slice(0, 7) === termMonth;
      if (hasExactLr) return normCn(r.lr_no) === normTerm || normCn(r.consignment_no) === normTerm;
      const numTerm = /^\d+$/.test(term);
      const numRe = numTerm ? new RegExp(`(^|[^0-9])${term}([^0-9]|$)`) : null;
      return [r.lr_no, r.consignment_no, r.origin, r.destination, r.customer_name, r.consignor, r.consignee, r.eway_bill, r.invoice_no, r.dc_no, ...(r.extra_invoices || []).map((x) => x.no), ...(r.extra_dcs || []).map((x) => x.no), ...(r.extra_eways || []).map((x) => x.no)]
        .filter(Boolean)
        .some((v) => (numTerm ? numRe.test(String(v).toLowerCase()) : String(v).toLowerCase().includes(term)));
    });
  }, [rows, q, status, fromDate, toDate, noBranchOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const podUrl = (r) => `${API}/files/${r.pod_path}?auth=${token}`;

  const exportCsv = () => {
    const headers = ["Branch", "LR No.", "LR Date", "From", "Destination", "Customer Name", "Customer Code", "Consignor Name", "Consignee Name", "Invoice No.", "Invoice Date", "Invoice Value", "DC No.", "DC Date", "DC Value", "Vehicle No.", "Vehicle Type", "Freight Charge", "Vehicle Hire", "Vehicle Advance", "Vehicle Balance", "Delivered By", "Vehicle Status"];
    const cn = (r) => String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "");
    const lines = [headers.join(",")];
    filtered.forEach((r) => {
      const h = hireMap[r.id] || {};
      const row = [r.office || r.office_name || "", cn(r), r.lr_date || r.dispatch_date || "", r.origin, r.destination, r.customer_name, codeFor(r), r.consignor, r.consignee, r.invoice_no || "", r.invoice_date || "", r.invoice_value || "", r.dc_no || "", r.dc_date || "", r.dc_value || "", r.vehicle_no || "", r.vehicle_type || findVehicle(r.vehicle_no)?.type || "", r.freight_amount || 0, h.vehicle_hire ?? "", h.vehicle_advance ?? "", h.vehicle_balance ?? "", r.delivered_by || "", r.status]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",");
      lines.push(row);
    });
    const dcTotal = filtered.reduce((s, r) => s + (Number(r.dc_value) || 0), 0);
    lines.push(["", "", "", "", "", "", "", "", "", "", "", "", "", "TOTAL", dcTotal, "", "", "", "", "", "", "", ""].map((v) => `"${v}"`).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `consignments_${fromDate || "all"}_${toDate || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmit = (e) => {
    e && e.preventDefault();
    const term = q.trim().toLowerCase();
    if (!term && !fromDate && !toDate) {
      toast.info("Enter a consignment number or pick a date range");
      return;
    }
    setHasSearched(true);
    // date-range search → just show the list, don't auto-open a single detail
    if (fromDate || toDate) {
      if (filtered.length === 0) toast.error("No consignments found in this date range");
      else toast.success(`${filtered.length} consignment(s) found`);
      return;
    }
    if (parseTermDate(term)) {
      if (filtered.length === 0) toast.error("No consignments on this date");
      else toast.success(`${filtered.length} consignment(s) on this date`);
      return;
    }
    if (parseTermMonth(term)) {
      if (filtered.length === 0) toast.error("No consignments in this month");
      else toast.success(`${filtered.length} consignment(s) in this month`);
      return;
    }
    const exact = (rows || []).find(
      (r) => normCn(r.consignment_no) === normCn(term) || normCn(r.lr_no) === normCn(term)
    );
    const numTerm = /^\d+$/.test(term);
    const target = exact || (!numTerm && filtered.length === 1 ? filtered[0] : null);
    setSuggestions([]);
    if (target) {
      setDetail(target);
    } else if (filtered.length === 0) {
      if (numTerm) {
        const n = parseInt(term, 10);
        const seen = new Set();
        const near = (rows || [])
          .map((r) => {
            const raw = String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "").trim();
            const v = parseInt(raw, 10);
            return isNaN(v) ? null : { v, raw, r };
          })
          .filter((x) => x && !seen.has(x.v) && seen.add(x.v))
          .sort((a, b) => Math.abs(a.v - n) - Math.abs(b.v - n))
          .slice(0, 5);
        setSuggestions(near);
      }
      toast.error(numTerm ? `No consignment found with LR No. ${term}` : "No consignment found for this number");
    } else {
      toast.success(`${filtered.length} consignment(s) matched${numTerm ? " by invoice / e-way / other fields (no LR with this number)" : ""} — showing in list`);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete consignment ${r.consignment_no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/shipments/${r.id}`);
      setRows((prev) => prev.filter((x) => x.id !== r.id));
      toast.success(`Consignment ${r.consignment_no} deleted`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader title="Consignment Enquiry" subtitle="Search and track any consignment by CN number, route, party or e-way bill." />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <form onSubmit={handleSubmit} className="flex flex-1 min-w-[240px] items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="enquiry-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Enter CN number, date (dd-mm-yyyy), city, customer…"
              className="pl-9"
            />
          </div>
          <Button type="submit" data-testid="enquiry-submit-btn">
            <Search className="mr-2 h-4 w-4" /> Submit
          </Button>
          <div className="flex items-center gap-1.5">
            <Label className="whitespace-nowrap text-xs text-muted-foreground">From</Label>
            <Input type="date" className="w-[150px]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="enquiry-from-date" />
            <Label className="whitespace-nowrap text-xs text-muted-foreground">To</Label>
            <Input type="date" className="w-[150px]" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="enquiry-to-date" />
          </div>
          <Button
            type="button"
            variant="outline"
            data-testid="enquiry-clear-btn"
            onClick={() => {
              setQ("");
              setStatus("All");
              setFromDate("");
              setToDate("");
              setHasSearched(false);
              setDetail(null);
              setSuggestions([]);
            }}
          >
            <X className="mr-2 h-4 w-4" /> Clear
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={`h-10 text-xs font-bold ${noBranchOnly ? "bg-amber-500 text-white hover:bg-amber-600" : "border border-amber-300 bg-white text-amber-700 hover:bg-amber-50"}`}
            onClick={() => { setNoBranchOnly((v) => !v); setHasSearched(true); }}
            data-testid="enquiry-no-branch-toggle"
          >
            {noBranchOnly ? "✓ " : ""}No Branch only ({(rows || []).filter((r) => !(r.office || r.office_name)).length})
          </Button>
        </form>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[180px]" data-testid="enquiry-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasSearched && filtered.length > 0 && (
          <div className="ml-auto flex gap-2">
            <Button variant="outline" className="gap-1.5" onClick={() => printConsignmentRegister(filtered.map((r) => ({ ...r, ...(hireMap[r.id] || {}), customer_code: codeFor(r), vehicle_type: r.vehicle_type || findVehicle(r.vehicle_no)?.type || "" })), fromDate, toDate)} data-testid="enquiry-print-list-btn">
              <Printer className="h-4 w-4" /> Print List
            </Button>
            <Button variant="outline" className="gap-1.5" onClick={exportCsv} data-testid="enquiry-excel-btn">
              <Download className="h-4 w-4" /> Excel
            </Button>
          </div>
        )}
      </div>

      {hasSearched && suggestions.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2" data-testid="lr-suggestions">
          <span className="text-sm font-semibold text-amber-800">LR No. {q.trim()} not found — did you mean:</span>
          {suggestions.map((s) => (
            <Button
              key={s.v}
              type="button"
              size="sm"
              variant="outline"
              className="h-7 bg-white px-3 font-bold"
              data-testid={`lr-suggestion-${s.raw}`}
              onClick={() => {
                setQ(s.raw);
                setSuggestions([]);
                setDetail(s.r);
              }}
            >
              {s.raw}
            </Button>
          ))}
        </div>
      )}

      {hasSearched ? (
        <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {["Branch", "LR No.", "LR Date", "From", "Destination", "Customer Name", "Customer Code", "Dispatch", "Freight Charge", "POD", "Delivered By", "Vehicle Status", "Prints"].map((h) => (
                <TableHead key={h} className={`text-[11px] uppercase tracking-[0.15em] text-slate-500 ${h === "Prints" ? "text-center" : ""}`}>
                  {h}
                </TableHead>
              ))}
              <TableHead className="w-[110px] text-right text-[11px] uppercase tracking-[0.15em] text-slate-500">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 14 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {rows && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={14} className="py-12 text-center text-muted-foreground">
                  No consignments match your search.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid="enquiry-row">
                <TableCell className="text-sm" data-testid="enquiry-branch-cell"><BranchCell value={r.office} /></TableCell>
                <TableCell className="font-medium">{String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "") || "—"}</TableCell>
                <TableCell className="text-sm">{r.lr_date || r.dispatch_date ? fmtDate(r.lr_date || r.dispatch_date) : "—"}</TableCell>
                <TableCell className="text-sm">{r.origin || "—"}</TableCell>
                <TableCell className="text-sm">{r.destination || "—"}</TableCell>
                <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                <TableCell className="text-sm" data-testid="enquiry-code-cell">{codeFor(r) || "—"}</TableCell>
                <TableCell className="text-sm">{fmtDate(r.dispatch_date)}</TableCell>
                <TableCell className="text-sm tabular-nums">{inr(r.freight_amount)}</TableCell>
                <TableCell>
                  {r.pod_path ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                      <FileCheck2 className="h-3.5 w-3.5" /> Yes
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-sm" data-testid="enquiry-delivered-by">{r.delivered_by || "—"}</TableCell>
                <TableCell>
                  <StatusBadge status={r.status} />
                </TableCell>
                <TableCell className="text-center text-sm" data-testid="lr-print-count">
                  {Number(r.print_count) > 0 ? (
                    <span className="inline-block min-w-[26px] rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700" title={r.last_printed_at ? `Last printed ${fmtDate(r.last_printed_at.slice(0, 10))}` : undefined}>
                      {r.print_count}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDetail(r)} data-testid="view-consignment-btn">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Delete consignment" onClick={() => remove(r)} data-testid="delete-consignment-btn">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableShell>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-slate-50/50 py-20 text-center" data-testid="enquiry-empty-state">
          <Search className="mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium text-slate-600">Enter a consignment number in the search bar and click Submit</p>
          <p className="mt-1 text-xs text-muted-foreground">The matching consignment details will appear here.</p>
        </div>
      )}

      {detail && (
        <div className="mt-6 rounded-xl border border-border bg-white p-6 shadow-sm" data-testid="enquiry-detail-panel">
          <div className="mb-3 flex items-center justify-between border-b pb-3">
            <h3 className="font-display text-lg font-semibold">
              Consignment No.{String(detail.lr_no || detail.consignment_no || "").replace(/^CN[-\s]?/i, "")}
            </h3>
            <Button variant="ghost" size="icon" onClick={() => setDetail(null)} data-testid="enquiry-detail-close">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid gap-x-10 sm:grid-cols-2">
            <div>
              <Row label="Branch" value={detail.office || detail.office_name} />
              <Row label="From" value={detail.origin} />
              <Row label="Destination" value={detail.destination} />
              <Row label="Consignor Name" value={detail.customer_name} />
              <Row label="Consignee Name" value={detail.consignee} />
              <Row label="Goods(Material)" value={detail.goods || detail.said_to_contain} />
              <Row label="E-way bill No." value={detail.eway_bill} />
              <Row
                label="E-way bill valid"
                value={
                  detail.eway_valid ? (
                    detail.eway_valid < new Date().toISOString().slice(0, 10) ? (
                      <span className="font-semibold text-red-600" data-testid="eway-expired">
                        {fmtDate(detail.eway_valid)} — EXPIRED
                      </span>
                    ) : (
                      fmtDate(detail.eway_valid)
                    )
                  ) : (
                    ""
                  )
                }
              />
              <Row label="Packages" value={detail.packages} />
              <Row label="Weight In KGS" value={detail.weight_kgs || detail.weight_tons} />
              <Row label="Vehicle Hire" value={billing?.lorry_hire ? inr(billing.lorry_hire) : detail.lorry_hire ? inr(detail.lorry_hire) : ""} />
              <Row label="Vehicle Advance" value={billing?.vehicle_advance ? inr(billing.vehicle_advance) : ""} />
              <Row label="Vehicle Balance" value={billing?.vehicle_balance || billing?.vehicle_balance === 0 ? inr(billing.vehicle_balance) : ""} />
              <Row
                label="Challan No."
                value={
                  billing?.challan_no ? (
                    <a
                      href={`/hire-challans?open=${billing.challan_id}`}
                      className="font-semibold text-primary underline underline-offset-2"
                      data-testid="enquiry-challan-link"
                    >
                      {String(billing.challan_no).replace(/^VHC[-\s]?/i, "")}
                    </a>
                  ) : (
                    ""
                  )
                }
              />
              <Row label="Challan Date" value={billing?.challan_date ? fmtDate(billing.challan_date) : ""} />
              <Row label="Vendor/Broker Name" value={billing?.vendor_broker_name || detail.vendor_name || ""} />
            </div>
            <div>
              <Row label="Invoice No." value={detail.invoice_no || ""} />
              <Row label="Invoice Date" value={detail.invoice_date ? fmtDate(detail.invoice_date) : ""} />
              <Row label="Invoice value" value={detail.invoice_value ? inr(detail.invoice_value) : ""} />
              <Row label="DC No." value={detail.dc_no || ""} />
              <Row label="DC Date" value={detail.dc_date ? fmtDate(detail.dc_date) : ""} />
              <Row label="DC Value" value={detail.dc_value ? inr(detail.dc_value) : ""} />
              <Row label="Freight Charge" value={inr(detail.freight_amount)} />
              <Row label="Vehicle No." value={detail.vehicle_no} />
              <Row label="Vehicle Type" value={detail.vehicle_type || findVehicle(detail.vehicle_no)?.type || ""} />
              <Row
                label="Driver Mobile No."
                value={(() => {
                  const ph = String(detail.driver_mobile || detail.driver_name || "").replace(/[^\d]/g, "");
                  if (!ph) return "";
                  return (
                    <span className="inline-flex items-center gap-2">
                      <a href={`tel:+91${ph.slice(-10)}`} className="font-medium text-primary underline underline-offset-2" data-testid="driver-call-link">
                        {detail.driver_mobile || detail.driver_name}
                      </a>
                      <a
                        href={`https://wa.me/91${ph.slice(-10)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="grid h-6 w-6 place-items-center rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                        title="WhatsApp driver"
                        data-testid="driver-whatsapp-link"
                      >
                        <MessageCircle className="h-3.5 w-3.5" />
                      </a>
                    </span>
                  );
                })()}
              />
              <Row
                label="Vehicle Owner Mobile No."
                value={(() => {
                  const ph = String(detail.owner_mobile || "").replace(/[^\d]/g, "");
                  if (!ph) return "";
                  return (
                    <span className="inline-flex items-center gap-2" data-testid="owner-mobile-value">
                      <a href={`tel:+91${ph.slice(-10)}`} className="font-medium text-primary underline underline-offset-2" data-testid="owner-call-link">
                        {detail.owner_mobile}
                      </a>
                      <a
                        href={`https://wa.me/91${ph.slice(-10)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="grid h-6 w-6 place-items-center rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                        title="WhatsApp vehicle owner"
                        data-testid="owner-whatsapp-link"
                      >
                        <MessageCircle className="h-3.5 w-3.5" />
                      </a>
                    </span>
                  );
                })()}
              />
              <Row label="Dispatch Date" value={fmtDate(detail.dispatch_date)} />
              <Row label="Delivery date" value={detail.delivery_date ? fmtDate(detail.delivery_date) : ""} />
              <Row label="Delivered by" value={detail.delivered_by ? `${detail.delivered_by}${detail.delivered_at ? " · " + fmtDate(detail.delivered_at) : ""}` : ""} />
              <Row label="Vehicle Status" value={detail.status} />
              <Row label="Remarks" value={detail.remarks} />
            </div>
          </div>
          <div className="mt-3 grid gap-x-10 border-t pt-2 sm:grid-cols-2" data-testid="enquiry-billing-info">
            <div>
              <Row label="Bill No." value={billing ? String(billing.bill_no || "").replace(/^INV[-\s]?/i, "") : "…"} />
              <Row label="Bill Date" value={billing?.bill_date ? fmtDate(billing.bill_date) : ""} />
              <Row label="Bill Amount" value={billing?.bill_amount ? inr(billing.bill_amount) : ""} />
            </div>
            <div>
              <Row label="MR No." value={billing ? String(billing.mr_no || "").replace(/^MR[-\s]?/i, "") : ""} />
              <Row label="MR Date" value={billing?.mr_date ? fmtDate(billing.mr_date) : ""} />
              <Row label="MR Amount" value={billing?.mr_amount ? inr(billing.mr_amount) : ""} />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button className="gap-2" onClick={() => printLrWithWatermark(detail, isAdmin, null, (n) => setRows((prev) => (prev || []).map((x) => (x.id === detail.id ? { ...x, print_count: n, last_printed_at: new Date().toISOString() } : x))))} data-testid="enquiry-print-btn">
              <Printer className="h-4 w-4" /> Print Consignment Note
            </Button>
            <Button
              variant="secondary"
              className="gap-2"
              onClick={() => printConsignmentDetail({ ...detail, vehicle_type: detail.vehicle_type || findVehicle(detail.vehicle_no)?.type || "" }, billing)}
              data-testid="enquiry-print-detail-btn"
            >
              <Printer className="h-4 w-4" /> Print Detail Sheet
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="enquiry-whatsapp-detail-btn"
              onClick={() => {
                const c = detail;
                const b = billing || {};
                const cust = customers.find((x) => (c.customer_id && x.id === c.customer_id) || (x.name || "").trim().toLowerCase() === String(c.customer_name || "").trim().toLowerCase());
                const digits = String(cust?.phone || "").replace(/\D/g, "");
                const lr = String(c.lr_no || c.consignment_no || "").replace(/^CN[-\s]?/i, "");
                const money = (v) => (v || v === 0 ? inr(v) : "");
                const line = (label, v) => (v ? `${label}: ${v}` : "");
                const msg = [
                  `*NISHA TRANSPORT CORPORATION — CONSIGNMENT DETAIL*`,
                  `*Consignment No. ${lr}*${c.lr_date || c.dispatch_date ? ` | LR Date: ${fmtDate(c.lr_date || c.dispatch_date)}` : ""}`,
                  ``,
                  line("Branch", c.office || c.office_name),
                  `From: ${c.origin || "—"} → To: ${c.destination || "—"}`,
                  line("Consignor", c.customer_name),
                  line("Consignee", c.consignee),
                  line("Goods(Material)", c.goods || c.said_to_contain),
                  line("Packages", c.packages),
                  line("Weight (KGS)", c.weight_kgs || c.weight_tons),
                  line("E-Way Bill", c.eway_bill),
                  ``,
                  line("Invoice No.", c.invoice_no),
                  line("Invoice Date", c.invoice_date ? fmtDate(c.invoice_date) : ""),
                  line("Invoice Value", money(c.invoice_value)),
                  line("DC No.", c.dc_no),
                  line("DC Date", c.dc_date ? fmtDate(c.dc_date) : ""),
                  line("DC Value", money(c.dc_value)),
                  line("Freight Charge", money(c.freight_amount)),
                  ``,
                  line("Vehicle No.", c.vehicle_no),
                  line("Vehicle Type", c.vehicle_type || findVehicle(c.vehicle_no)?.type),
                  line("Challan No.", String(b.challan_no || "").replace(/^VHC[-\s]?/i, "")),
                  line("Challan Date", b.challan_date ? fmtDate(b.challan_date) : ""),
                  line("Dispatch Date", c.dispatch_date ? fmtDate(c.dispatch_date) : ""),
                  line("Delivery Date", c.delivery_date ? fmtDate(c.delivery_date) : ""),
                  `Status: ${c.status || "Pending"}`,
                  c.pod_path && podShareId ? `POD copy: ${API}/public/pod/${podShareId}` : "",
                  ``,
                  line("Bill No.", String(b.bill_no || "").replace(/^INV[-\s]?/i, "")),
                  line("Bill Date", b.bill_date ? fmtDate(b.bill_date) : ""),
                  line("Bill Amount", money(b.bill_amount)),
                  line("MR No.", String(b.mr_no || "").replace(/^MR[-\s]?/i, "")),
                  line("MR Amount", money(b.mr_amount)),
                  ``,
                  `— Nisha Transport Corporation`,
                ].filter((l, i, a) => l !== "" || (a[i - 1] !== "" && a[i + 1] !== "")).join("\n");
                const target = digits ? (digits.length === 10 ? "91" + digits : digits) : "";
                window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
              }}
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp Detail
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="enquiry-whatsapp-btn"
              onClick={() => {
                const c = detail;
                const msg = encodeURIComponent(
                  [
                    `*NISHA TRANSPORT CORPORATION — LR STATUS*`,
                    `LR No.: ${c.lr_no || c.consignment_no || ""} | Date: ${c.lr_date ? fmtDate(c.lr_date) : ""}`,
                    `From: ${c.origin || ""} → To: ${c.destination || ""}`,
                    `Consignor: ${c.consignor || ""}`,
                    `Consignee: ${c.consignee || ""}`,
                    c.vehicle_no ? `Vehicle No.: ${c.vehicle_no}` : "",
                    c.eway_bill ? `E-Way Bill: ${c.eway_bill}` : "",
                    `Status: ${c.status || "Pending"}${c.delivery_date ? ` (Delivered on ${fmtDate(c.delivery_date)})` : ""}`,
                    ``,
                    `Track & trade enquiries: ${"+91 93518 80118"}`,
                  ].filter(Boolean).join("\n")
                );
                window.open(`https://wa.me/?text=${msg}`, "_blank");
              }}
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp Status
            </Button>
            {(() => {
              const veh = findVehicle(detail.vehicle_no);
              if (!veh) return null;
              return (
                <>
                  {veh.gps_link && (
                    <a href={veh.gps_link} target="_blank" rel="noreferrer" data-testid="enquiry-track-gps">
                      <Button variant="outline" className="gap-2">
                        <Satellite className="h-4 w-4 text-primary" /> Track GPS
                      </Button>
                    </a>
                  )}
                  {veh.fastag_link && (
                    <a href={veh.fastag_link} target="_blank" rel="noreferrer" data-testid="enquiry-track-fastag">
                      <Button variant="outline" className="gap-2">
                        <Radio className="h-4 w-4 text-primary" /> Track FASTag
                      </Button>
                    </a>
                  )}
                </>
              );
            })()}
            {detail.pod_path && (
              <a href={podUrl(detail)} target="_blank" rel="noreferrer" data-testid="enquiry-view-pod">
                <Button variant="outline" className="gap-2">
                  <FileCheck2 className="h-4 w-4 text-emerald-600" /> View POD ({detail.pod_filename})
                </Button>
              </a>
            )}
            {(detail.customer_name || detail.consignor) && (
              <Button
                variant="outline"
                className="gap-2 border-sky-300 text-sky-700 hover:bg-sky-50"
                data-testid="enquiry-pod-portal-btn"
                onClick={async () => {
                  try {
                    const r = await api.post("/pod-portal", { customer_name: detail.customer_name || detail.consignor });
                    const link = `${API}/public/pod-portal/${r.data.id}`;
                    navigator.clipboard?.writeText(link);
                    window.open(link, "_blank");
                    toast.success(r.data.last_visited_at ? `Portal link copied & opened — customer last opened ${fmtDate(String(r.data.last_visited_at).slice(0, 10))}` : "Portal link copied & opened — not opened by customer yet");
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail));
                  }
                }}
              >
                <ExternalLink className="h-4 w-4" /> Customer POD Portal
              </Button>
            )}
          </div>
          {detail.delivery_otp_confirmed && (
            <div className="mt-3">
              <span className="inline-flex -rotate-1 items-center gap-1.5 rounded-md border-2 border-emerald-600 px-3 py-1 text-xs font-extrabold tracking-wider text-emerald-700" data-testid="enquiry-otp-stamp">
                <ShieldCheck className="h-4 w-4" /> DELIVERY CONFIRMED BY CONSIGNEE OTP{detail.delivery_otp_confirmed_at ? ` — ${fmtDate(String(detail.delivery_otp_confirmed_at).slice(0, 10))}` : ""}
              </span>
            </div>
          )}
          {detail.pod_path && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="enquiry-pod-image-panel">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">POD — {detail.pod_filename}</div>
              {String(detail.pod_filename || "").toLowerCase().endsWith(".pdf") ? (
                <a href={podUrl(detail)} target="_blank" rel="noreferrer" className="text-sm font-medium text-primary underline underline-offset-2" data-testid="enquiry-pod-pdf-link">
                  Open POD PDF
                </a>
              ) : (
                <a href={podUrl(detail)} target="_blank" rel="noreferrer" data-testid="enquiry-pod-image-link">
                  <img src={podUrl(detail)} alt="POD" className="max-h-72 rounded-md border border-slate-200 bg-white object-contain" data-testid="enquiry-pod-image" />
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
